// The portfolio's menu, at the portal's size.
// Closed: the white pill with the four-dot diamond, top right (bottom right on
// phones). Open: one shape, the button lets the panel show through while the
// panel grows to black, the diamond turns 45° and goes white, the links slide
// in. Everything behind sits under one 30px blur; clicking it or Esc closes.
// The type is smaller than the portfolio's big menu words: Blubird Bold
// Display at 22px for projects, mono for the rest.
import * as React from "react"
import { Dots } from "../toolkit/Dots"
import { EASE, freeze, reduced } from "../toolkit/motion"
import type { Loaded } from "./api"
import { Link, type Route } from "./route"
import { status } from "./status"
import { t } from "./i18n"

export function Menu({ data, route, onLogout }: { data: Loaded; route: Route; onLogout: () => void }) {
    const [open, setOpen] = React.useState(false)
    const [idle, setIdle] = React.useState(true) // closed and settled: the button's own white
    const inner = React.useRef<HTMLDivElement>(null)
    const root = React.useRef<HTMLDivElement>(null)

    // the open height follows the content
    React.useLayoutEffect(() => {
        const el = inner.current
        if (!el) return
        const set = () => root.current?.style.setProperty("--m-h", el.scrollHeight + "px")
        set()
        const ro = new ResizeObserver(set)
        ro.observe(el)
        return () => ro.disconnect()
    }, [data])

    const toggle = (to = !open) => {
        if (to === open) return
        setOpen(to)
        if (to) {
            setIdle(false)
            const items = inner.current?.querySelectorAll<HTMLElement>("[data-m]")
            items?.forEach((it, i) => {
                freeze(it)
                it.animate(
                    [
                        { opacity: 0, transform: reduced() ? "none" : "translateX(24px)" },
                        { opacity: 1, transform: "none" },
                    ],
                    { duration: 420, delay: 230 + i * 55, easing: EASE.slowDown, fill: "backwards" }
                )
            })
        }
    }

    // closing: settles, then the button takes its white back
    React.useEffect(() => {
        if (open) return
        const t = window.setTimeout(() => setIdle(true), reduced() ? 0 : 520)
        return () => window.clearTimeout(t)
    }, [open])

    React.useEffect(() => {
        if (!open) return
        const key = (e: KeyboardEvent) => e.key === "Escape" && toggle(false)
        addEventListener("keydown", key)
        return () => removeEventListener("keydown", key)
    }, [open])

    // a page change closes it
    React.useEffect(() => toggle(false), [route.project, route.tab, route.client])

    const close = () => toggle(false)

    return (
        <div ref={root} className="m" data-open={open} data-idle={idle || undefined}>
            <div className="m-veil" onClick={close} aria-hidden="true" />
            <div className="m-wrap">
                <nav
                    className="m-panel"
                    aria-hidden={!open}
                    inert={!open ? ("" as any) : undefined}
                    onClickCapture={(e) => {
                        // a link closes the menu at once, while the page changes under it
                        const a = (e.target as HTMLElement).closest("a")
                        if (a && !a.target) close()
                    }}
                >
                    <div ref={inner} className="m-inner">
                        <div className="m-top" data-m>
                            <span className="nto-label">{data.name}</span>
                        </div>
                        <Link className="m-all" to={{ client: route.client, tab: "planning" }} data-m data-here={!route.project || undefined}>
                            <span className="nto-mono">{t("All projects")}</span>
                            <span className="m-count nto-mono">{data.projects.length}</span>
                        </Link>
                        <ul className="m-list">
                            {data.projects.map((e) => {
                                const here = e.project.id === route.project
                                return (
                                    <li key={e.project.id} data-m>
                                        <Link className="m-link" to={{ client: route.client, project: e.project.id, tab: "planning" }} data-here={here || undefined}>
                                            <span className="m-arrow">
                                                <Dots shape="arrow" />
                                            </span>
                                            <span className="m-title">{e.project.title}</span>
                                            <span className="m-sub nto-mono">
                                                {e.project.reference} · {status(e).text}
                                            </span>
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                        <div className="m-foot nto-mono" data-m>
                            <button
                                onClick={() => {
                                    close()
                                    onLogout()
                                }}
                            >
                                {t("Log out")}
                            </button>
                            <a href="https://nascimen.to" target="_blank" rel="noopener">
                                nascimen.to
                            </a>
                        </div>
                    </div>
                </nav>
                <button className="m-btn" onClick={() => toggle()} aria-expanded={open} aria-label={open ? "Close menu" : "Menu"}>
                    <Dots shape="diamond" className="m-icon" />
                </button>
            </div>
        </div>
    )
}
